import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  MagnifyingGlass,
  Briefcase,
  Calendar,
  MapPin,
  ArrowRight,
  Package,
  FileText,
} from '@phosphor-icons/react';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await projectAPI.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { label: 'Borrador', className: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
      sent: { label: 'Enviado', className: 'bg-blue-100 text-blue-700 border-blue-300' },
      in_progress: { label: 'En Progreso', className: 'bg-amber-100 text-amber-700 border-amber-300' },
      completed: { label: 'Completado', className: 'bg-green-100 text-green-700 border-green-300' },
      cancelled: { label: 'Cancelado', className: 'bg-red-100 text-red-700 border-red-300' },
    };
    const config = statusMap[status] || { label: status, className: 'bg-zinc-100 text-zinc-700' };
    return <Badge variant="outline" className={`rounded-none ${config.className}`}>{config.label}</Badge>;
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          project.key?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || project.status === activeTab;
    return matchesSearch && matchesTab;
  });

  const statusCounts = {
    all: projects.length,
    draft: projects.filter(p => p.status === 'draft').length,
    sent: projects.filter(p => p.status === 'sent').length,
    in_progress: projects.filter(p => p.status === 'in_progress').length,
    completed: projects.filter(p => p.status === 'completed').length,
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="h-10 w-48 bg-zinc-200 animate-pulse"></div>
          <div className="h-10 w-36 bg-zinc-200 animate-pulse"></div>
        </div>
        <div className="grid gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-40 bg-zinc-200 animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Proyectos</h1>
          <p className="text-zinc-600">Gestiona tus proyectos y cotizaciones</p>
        </div>
        <Link to="/projects/new">
          <Button className="btn-primary rounded-none" data-testid="new-project-btn">
            <Plus className="w-5 h-5 mr-2" />
            Nuevo Proyecto
          </Button>
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
          <Input
            placeholder="Buscar proyectos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-none"
            data-testid="search-projects"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-100 rounded-none p-1 border border-zinc-200">
          <TabsTrigger value="all" className="rounded-none data-[state=active]:bg-white">
            Todos ({statusCounts.all})
          </TabsTrigger>
          <TabsTrigger value="draft" className="rounded-none data-[state=active]:bg-white">
            Borradores ({statusCounts.draft})
          </TabsTrigger>
          <TabsTrigger value="sent" className="rounded-none data-[state=active]:bg-white">
            Enviados ({statusCounts.sent})
          </TabsTrigger>
          <TabsTrigger value="in_progress" className="rounded-none data-[state=active]:bg-white">
            En Progreso ({statusCounts.in_progress})
          </TabsTrigger>
          <TabsTrigger value="completed" className="rounded-none data-[state=active]:bg-white">
            Completados ({statusCounts.completed})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredProjects.length === 0 ? (
            <Card className="border-zinc-200 rounded-none">
              <CardContent className="p-12 text-center">
                <Package className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">
                  {searchQuery ? 'No se encontraron proyectos' : 'No tienes proyectos aún'}
                </h3>
                <p className="text-zinc-500 mb-6">
                  {searchQuery 
                    ? 'Intenta con otros términos de búsqueda' 
                    : 'Crea tu primer proyecto para comenzar a recibir cotizaciones'
                  }
                </p>
                {!searchQuery && (
                  <Link to="/projects/new">
                    <Button className="btn-primary rounded-none">
                      <Plus className="w-5 h-5 mr-2" />
                      Crear Proyecto
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredProjects.map((project) => (
                <Link 
                  key={project.project_id} 
                  to={`/projects/${project.project_id}`}
                  data-testid={`project-card-${project.project_id}`}
                >
                  <Card className="border-zinc-200 rounded-none hover:shadow-[4px_4px_0px_0px_rgba(9,9,11,1)] transition-shadow cursor-pointer">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Briefcase className="w-5 h-5 text-amber-500" />
                            <h3 className="text-lg font-bold text-zinc-900">{project.name}</h3>
                            {getStatusBadge(project.status)}
                          </div>
                          
                          <p className="text-sm text-zinc-500 mb-3">
                            Clave: <span className="font-mono text-zinc-700">{project.key}</span>
                          </p>

                          <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-600">
                            <span className="flex items-center gap-1">
                              <Package className="w-4 h-4" />
                              {project.total_materials} materiales
                            </span>
                            <span className="flex items-center gap-1">
                              <FileText className="w-4 h-4" />
                              {project.total_categories} categorías
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(project.deadline).toLocaleDateString('es-MX')}
                            </span>
                            {project.delivery_location?.address && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {project.delivery_location.address.substring(0, 30)}...
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-3">
                          {project.sub_quotations_count > 0 && (
                            <div className="w-48">
                              <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
                                <span>Progreso</span>
                                <span>{project.sub_quotations_with_winner}/{project.sub_quotations_count}</span>
                              </div>
                              <Progress 
                                value={(project.sub_quotations_with_winner / project.sub_quotations_count) * 100} 
                                className="h-2 rounded-none"
                              />
                            </div>
                          )}
                          <Button variant="ghost" className="rounded-none">
                            Ver Detalles
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProjectsPage;
